import { useEffect, useState } from 'react';

import { supabaseAnon } from '../lib/supabase';

interface MaintenanceState {
  maintenanceMode: boolean;
  /**
   * `require2faSuperAdmin` z tego samego wiersza `system_settings.global`.
   * Do 13.09.2026 przełącznik w ustawieniach super-admina zapisywał flagę,
   * której nic nie czytało („egzekwowanie w kolejnej iteracji"); czyta ją
   * ProtectedRoute przez `dokadPrzedTrasa` (lib/dwuskladnikowe.ts).
   */
  require2faSuperAdmin: boolean;
  isLoading: boolean;
}

interface FlagiGlobalne {
  maintenanceMode: boolean;
  require2faSuperAdmin: boolean;
}

const FLAGI_DOMYSLNE: FlagiGlobalne = { maintenanceMode: false, require2faSuperAdmin: false };

// Resolve to "not in maintenance" if the read takes longer than this. Because
// ProtectedRoute blocks on this lookup before rendering anything, a never-
// settling read would otherwise pin every protected route on the spinner.
const FETCH_TIMEOUT_MS = 5_000;

// The global maintenance flag lives in public.system_settings (key='global',
// value.maintenanceMode). It changes rarely, so the lookup is cached at module
// scope and resolved once per app load rather than on every navigation /
// ProtectedRoute remount. (A super-admin who toggles it is unaffected — they
// bypass the gate — and other users pick up the new value on their next load.)
let cachedPromise: Promise<FlagiGlobalne> | null = null;

async function fetchMaintenanceMode(): Promise<FlagiGlobalne> {
  try {
    // system_settings is public-readable, so use the anon client. The shared
    // authenticated client can block PostgREST calls while auth init/getSession
    // hangs — which would defeat the fail-open behaviour below and leave the app
    // stuck on the maintenance spinner. supabaseAnon has auth disabled.
    const queryPromise = supabaseAnon
      .from('system_settings')
      .select('value')
      .eq('key', 'global')
      .maybeSingle()
      .then(({ data, error }): FlagiGlobalne => {
        if (error) return FLAGI_DOMYSLNE;
        const value = (data?.value ?? {}) as {
          maintenanceMode?: boolean;
          require2faSuperAdmin?: boolean;
        };
        return {
          maintenanceMode: value.maintenanceMode === true,
          require2faSuperAdmin: value.require2faSuperAdmin === true,
        };
      });

    // Fail open on timeout: a hung read must never lock the platform out.
    const timeoutPromise = new Promise<FlagiGlobalne>((resolve) =>
      setTimeout(() => resolve(FLAGI_DOMYSLNE), FETCH_TIMEOUT_MS)
    );

    return await Promise.race([queryPromise, timeoutPromise]);
  } catch {
    // Fail open: a transient error reading settings must never lock the whole
    // platform out. Treat an unreadable flag as "not in maintenance".
    return FLAGI_DOMYSLNE;
  }
}

/**
 * Reads the platform-wide maintenance flag. Consumed by ProtectedRoute to block
 * non-super-admin access while maintenance mode is on.
 */
export function useMaintenanceMode(): MaintenanceState {
  const [flagi, setFlagi] = useState<FlagiGlobalne>(FLAGI_DOMYSLNE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!cachedPromise) {
      cachedPromise = fetchMaintenanceMode();
    }
    cachedPromise
      .then((value) => {
        if (active) {
          setFlagi(value);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return {
    maintenanceMode: flagi.maintenanceMode,
    require2faSuperAdmin: flagi.require2faSuperAdmin,
    isLoading,
  };
}
